import { Injectable } from '@angular/core';
import { AbsentismoDiario } from 'app/modules/common/entities/absentismodiario.model';
import { AbsentismoMensual } from 'app/modules/common/entities/absentismomensual.model';
import { LanguageService } from 'app/modules/common/services/language-service';
import { ChartDataset } from 'chart.js';
import _ from 'lodash';
import moment, { Moment } from 'moment';
import { CompanyDashboardService } from './company.dashboard.service';

/**
 * Dashboard Charts Data
 */
export interface DashboardChartsData {
    absentismoDiarioChart: ChartDataset[];
    absentismoMensualChart: ChartDataset[];
    absentismoAcumuladoChart: ChartDataset[];
    bajasNuevasComparativoChart: ChartDataset[];
    tasaIncidenciaChart: ChartDataset[];
    tasaPrevalenciaChart: ChartDataset[];
    bajasNuevasChart: ChartDataset[];
    mediaBajasDiariasChart: ChartDataset[];
    plantillaChart: ChartDataset[];
    absentismoComparativoChart: ChartDataset[];
}

/**
 * Dashboard Charts Labels
 */
interface DasboardChartsLabels {
    allMonthsLabels: string[];
    daysLabelsOfMonthlyCharts: string[];
    absentismoDiarioChartLabels: string[];
}


/**
 * Selects a numeric property from the monthly absentism data
 */
type MonthlyDataPropertySelector = (monthlyData: AbsentismoMensual) => number;


@Injectable({
    providedIn: 'root'
})
export class DashboardChartsService {


    /**
     * Chart properties
     */
    public get chartProperties() {
        return this._chartProperties;
    }
    private _chartProperties = {
        showLegends: false,
        chartOptions: {
            basic: {
                scaleShowVerticalLines: false,
                responsive: true,
                fill: false,
            },
            floatingYAxis: {
                scaleShowVerticalLines: false,
                responsive: true,
                fill: false,
                scales: {
                    yAxes: [
                        {
                            ticks: {
                                callback: (label: number, _index: any, _labels: any) => {
                                    return label.toFixed(1);
                                }
                            }
                        }
                    ]
                }
            },
            percentageYAxis: {
                scaleShowVerticalLines: false,
                responsive: true,
                fill: false,
                scales: {
                    yAxes: [
                        {
                            ticks: {
                                callback: (label: number, _index: any, _labels: any) => {
                                    return `${label}%`;
                                }
                            }
                        }
                    ]
                }
            }
        }
    };

    /**
     * Labels for charts
     */
    public get chartLabels(): DasboardChartsLabels {
        return this._chartLabels;
    }
    private _chartLabels: DasboardChartsLabels = {
        allMonthsLabels: [],
        daysLabelsOfMonthlyCharts: [],
        absentismoDiarioChartLabels: [],
    };

    /**
     * Chart Data
     */
    public get chartData(): DashboardChartsData {
        return this._chartData;
    }
    private _chartData: DashboardChartsData = {
        absentismoDiarioChart: [{
            data: [], borderColor: '#263d73', fill: false, pointHoverBackgroundColor: 'red', pointRadius: 5, pointHoverRadius: 8
        }],
        absentismoMensualChart: [{
            data: [], borderColor: '#263d73', fill: false, pointHoverBackgroundColor: 'red', pointRadius: 5, pointHoverRadius: 8
        }],
        absentismoAcumuladoChart: [{
            data: [], borderColor: '#263d73', fill: false, pointHoverBackgroundColor: 'red', pointRadius: 5, pointHoverRadius: 8
        }],
        bajasNuevasComparativoChart: [
            { data: [], backgroundColor: '#263d73', hoverBackgroundColor: '#1c2c52' }
        ],
        tasaIncidenciaChart: [
            { data: [], borderColor: '#263d73', fill: false, pointHoverBackgroundColor: 'red', pointRadius: 5, pointHoverRadius: 8 }
        ],
        tasaPrevalenciaChart: [
            { data: [], borderColor: '#263d73', fill: false, pointHoverBackgroundColor: 'red', pointRadius: 5, pointHoverRadius: 8 },
        ],
        bajasNuevasChart: [
            { data: [], backgroundColor: '#263d73', hoverBackgroundColor: '#1c2c52' },
        ],
        mediaBajasDiariasChart: [
            { data: [], borderColor: '#263d73', fill: false, pointHoverBackgroundColor: 'red', pointRadius: 5, pointHoverRadius: 8 },
        ],
        plantillaChart: [
            { data: [], borderColor: '#263d73', fill: false, pointHoverBackgroundColor: 'red', pointRadius: 5, pointHoverRadius: 8 },
        ],
        absentismoComparativoChart: [{
            data: [], borderColor: '#263d73', fill: false, pointHoverBackgroundColor: 'red', pointRadius: 5, pointHoverRadius: 8
        }]
    };



    constructor(
        private dashboardService: CompanyDashboardService,
        private languageService: LanguageService,
    ) {
        // set month names as chart labels
        this._chartLabels.allMonthsLabels = _.range(12).map(n => this.languageService
            .getMonthInSpanish(n)
            .substr(0, 3)
            .toLowerCase()
        );
    }


    /**
     * Load chart data from API for the given date and empressa ID. Then update the chart data sources and labels  
     * Bind chart data sources and labels into your view. Call this function whenever user selects a date
     * @param empressaId Company ID
     * @param date User selected date
     */
    public feedData = (empressaId: number, date: Moment) => {

        // load absentism diario chart data
        this.loadAbsentismDiarioChartData(empressaId, date);

        // load absentism mensual chart data
        this.loadMensualChartsData(empressaId);

        // load absentism comparativo chart data
        this.loadComparativeChartsData(empressaId);

    };



    //#region SET CHART DATA


    /**
     * Load data from API into Diario Chart datasource and set chart labels
     * @param empressaId Company ID
     * @param selectedDate User selected date
     */
    private loadAbsentismDiarioChartData = (empressaId: number, selectedDate: Moment) => {

        // start date is 60 days before selected date
        const startDate = selectedDate.clone().subtract({ days: 60 });
        const endDate = selectedDate.clone();

        // set chart labels
        this._chartLabels.absentismoDiarioChartLabels = this.getDaysBetweenDays(startDate, endDate);

        // call API
        this.dashboardService.getAbsentismoDiarioDataForPeriod(empressaId, startDate, endDate)
            .subscribe(res => {

                if (res.code == 0) {

                    // convert null integers to zero
                    this.dashboardService.convertNullNumbersToZero(res.result);

                    // set chart data
                    this.setAbsentismDiarioChartData(res.result);

                }
                else {
                    console.error(res.message);
                }

            }, err => {
                console.error(err);
            });

    };


    /**
     * Set given data into Absentism Diario Chart datasource
     * @param data Data for Absentism Diario Chart
     */
    private setAbsentismDiarioChartData = (data: AbsentismoDiario[]) => {

        // if data is null, set it to an empty array
        if (data == null)
            data = [];

        // get absentismo from data set
        const chartData = data.map(d => d.absentismo);

        // update chart data source
        this._chartData.absentismoDiarioChart[0].data = chartData;

    };


    /**
     * Load data from API into all Mensual type Charts datasource and set chart labels  
     * Mensual type charts include : Absentismo mensual, Bajas nuevas, Media bajas diarias, Plantilla
     * @param empressaId Company ID
     */
    private loadMensualChartsData = (empressaId: number) => {

        // call API
        this.dashboardService.getAbsentismoMensualDataForYears(empressaId, 2, false)
            .subscribe(res => {

                if (res.code == 0) {

                    // convert null integers to zero
                    this.dashboardService.convertNullNumbersToZero(res.result);

                    // set table data
                    this.setMensualChartsData(res.result);

                }
                else {
                    console.error(res.message);
                }

            }, err => {
                console.error(err);
            });

    };


    /**
     * Set given data into all Mensual type Charts datasource and set chart labels  
     * Mensual type charts include : Absentismo mensual, Bajas nuevas, Media bajas diarias, Plantilla
     * @param data Data for Absentism Diario Chart
     */
    private setMensualChartsData = (data: AbsentismoMensual[]) => {

        // if data is null, set it to an empty array
        if (data == null)
            data = [];

        // set chart labels
        // this._chartLabels.daysLabelsOfMonthlyCharts = this.getChartLabelsFromMonthlyData(data);
        this._chartLabels.daysLabelsOfMonthlyCharts =
            this.getMonthsBetweenYears(moment().subtract({ years: 2 }), moment());

        // Set Absentismo Mensual Chart Data
        this._chartData.absentismoMensualChart[0].data = data.map(d => d.absentismo);

        // Set Bajas Nuevas Chart Data
        this._chartData.bajasNuevasChart[0].data = data.map(d => d.bajasNuevas);

        // Set Media bajas diarias chart data
        this._chartData.mediaBajasDiariasChart[0].data = data.map(d => d.mediaBajaDiaria);

        // Set Plantilla chart data
        this._chartData.plantillaChart[0].data = data.map(d => d.plantilla);

    };


    /**
     * Load data into all Comparativo Charts datasource and set labels
     * Comparative charts include : Absentismo comparativo, Absentismo acumulado, Bajas nuevas comparativo, Tasa de incidencia,
     * Tasa de prevalencia
     * @param empressaId Company ID
     */
    private loadComparativeChartsData = (empressaId: number) => {

        // call API
        this.dashboardService.getAbsentismoMensualDataForYears(empressaId, 5, true)
            .subscribe(res => {

                if (res.code == 0) {

                    // convert null integers to zero
                    this.dashboardService.convertNullNumbersToZero(res.result);

                    // set table data
                    this.setComparativeChartsData(res.result);

                }
                else {
                    console.error(res.message);
                }

            }, err => {
                console.error(err);
            });

    };


    /**
     * Set given data into all Comparativo Charts datasource  
     * Comparative charts include : Absentismo comparativo, Absentismo acumulado, Bajas nuevas comparativo, Tasa de incidencia,
     * Tasa de prevalencia
     * @param data Data for any Comparativo Chart
     */
    private setComparativeChartsData = (data: AbsentismoMensual[]) => {

        // if data is null, set it to an empty array
        if (data == null)
            data = [];

        // Set Absentismo Comparativo chart data
        this._chartData.absentismoComparativoChart = this.groupMonthlyDataIntoYears(data, (d) => d.absentismo);

        // Set Absentismo Acumulado chart data
        this._chartData.absentismoAcumuladoChart = this.groupMonthlyDataIntoYears(data, d => d.absAcumulado);

        // Set Bajas Nuevas Comparativo chart data
        this._chartData.bajasNuevasComparativoChart = this.groupMonthlyDataIntoYears(data, d => d.bajasNuevas);

        // Set Tasa De Prevalencia
        this._chartData.tasaIncidenciaChart = this.groupMonthlyDataIntoYears(data, d => d.tasaIncidencia);

        // Set Tasa De Prevalencia
        this._chartData.tasaPrevalenciaChart = this.groupMonthlyDataIntoYears(data, d => d.tasaPrevalencia);

    };


    //#endregion



    //#region UTILS

    /**
     * Gets days between given period
     * @param startDate Start date
     * @param endDate End date
     * @returns Array of strings containing days between given period in the format `DD/MM`
     */
    private getDaysBetweenDays = (startDate: Moment, endDate: Moment) => {

        // clone start date
        let currentDate = startDate.clone();

        // store each date
        const dates: string[] = [];

        while (true) {

            // add date in a format
            dates.push(currentDate.format("DD/MM"));

            // increment date by one day
            currentDate.add({ days: 1 });

            // exit if current date is greater than end date
            if (currentDate.isAfter(endDate))
                break;

        }

        return dates;

    };


    /**
     * Group monthly absentism data according to year and series of data
     * @param data Monthly absentism data
     * @param propertySelector Select a property from monthly data to add into chart
     * @returns Chart data with each year as a series
     */
    private groupMonthlyDataIntoYears = (data: AbsentismoMensual[], propertySelector: MonthlyDataPropertySelector): ChartDataset[] => {

        // group data by year
        const groups = _.groupBy(data, (d) => d.anyo);

        // add each series
        const chartData: ChartDataset[] = [];

        for (const year in groups) {
            const yearData = groups[year].map(propertySelector);

            chartData.push({
                data: yearData, label: year, borderColor: '#263d73', fill: false, pointHoverBackgroundColor: 'red',
                pointRadius: 5, pointHoverRadius: 8
            });
        }

        // if array is empty create an empty data for chart
        if (chartData.length === 0) {
            chartData.push({
                data: [], borderColor: '#263d73', fill: false, pointHoverBackgroundColor: 'red',
                pointRadius: 5, pointHoverRadius: 8
            });
        }

        return chartData;

    };


    /**
     * Get chart labels from mothly data. Mes and Anyo properties will be formatted as mm/yy and put into an array
     * @param data Monthly Absentism data
     */
    private getChartLabelsFromMonthlyData = (data: AbsentismoMensual[]) => {

        const labels = data.map(d => {
            const mes = d.mes.toString().padStart(2, "0");
            const anyo = d.anyo.toString().substr(2, 2);

            return `${mes}/${anyo}`;
        });

        return labels;

    };


    /**
     * Gets months between given period
     * @param startDate Start date
     * @param endDate End date
     * @returns Array of strings containing months between given period in the format `MM/YY`
     */
    private getMonthsBetweenYears = (startDate: Moment, endDate: Moment) => {

        // clone start date
        let currentDate = startDate.clone();

        // store each date
        const months: string[] = [];

        while (true) {

            // add date in a format
            months.push(currentDate.format("MM/YY"));

            // increment date by one day
            currentDate.add({ months: 1 });

            // exit if current date is greater than end date
            if (currentDate.isAfter(endDate))
                break;

        }

        return months;

    };

    //#endregion


}
